import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import {
  CREDIT_OPERATIONS_DATE_COLUMN_INDEXES,
  CREDIT_OPERATIONS_TABLE_ARIA_LABEL,
  CREDIT_OPERATIONS_TABLE_COLUMN_WIDTHS,
  CREDIT_OPERATIONS_TABLE_HEADERS,
  CREDIT_OPERATIONS_TABLE_MIN_WIDTH,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/creditOperations/constants.ts';
import { CreditOperationRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/creditOperations/CreditOperationRow.tsx';
import { CreditOperationsTotals } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/creditOperations/CreditOperationsTotals.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import type { ICreditOperationsSectionProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/creditOperations/types.ts';
import { useDocumentGenerationOptions } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/hooks/useDocumentGenerationOptions.ts';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import {
  createCreditOperation,
  getCreditOperationTotals,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const CreditOperationsSection: React.FC<ICreditOperationsSectionProps> = ({ disabled }) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const borrowerType = watch('borrowerType')?.value;
  const hasNoCreditOperations = watch('hasNoCreditOperations');
  const creditOperations = watch('creditOperations');
  const { fields, append, remove } = useFieldArray({ control, name: 'creditOperations' });
  const { shortBankOptions, currencyOptions, isBanksLoading, isCurrenciesLoading } =
    useDocumentGenerationOptions();
  const totals = getCreditOperationTotals(creditOperations);

  if (!borrowerType) {
    return null;
  }

  return (
    <Box sx={documentGenerationFormStyles.section}>
      <Typography component="h3" sx={documentGenerationFormStyles.sectionTitle}>
        3. Інформація про кредитні операції в інших банках та небанківських фінансових установах
      </Typography>
      <FormCheckbox
        name="hasNoCreditOperations"
        label="Кредитних операцій в інших установах немає"
        disabled={disabled}
        formControlLabelProps={{ sx: documentGenerationFormStyles.checkbox }}
      />

      {!hasNoCreditOperations && (
        <>
          <FinancialConditionTable
            ariaLabel={CREDIT_OPERATIONS_TABLE_ARIA_LABEL}
            showActions={fields.length > 1}
            dateColumnIndexes={[...CREDIT_OPERATIONS_DATE_COLUMN_INDEXES]}
            minWidth={CREDIT_OPERATIONS_TABLE_MIN_WIDTH}
            columnWidths={[...CREDIT_OPERATIONS_TABLE_COLUMN_WIDTHS]}
            headers={[...CREDIT_OPERATIONS_TABLE_HEADERS]}
          >
            {fields.map((field, index) => (
              <CreditOperationRow
                key={field.id}
                index={index}
                canRemove={fields.length > 1}
                disabled={disabled}
                lenderOptions={shortBankOptions}
                currencyOptions={currencyOptions}
                isBanksLoading={isBanksLoading}
                isCurrenciesLoading={isCurrenciesLoading}
                onRemove={() => remove(index)}
              />
            ))}
          </FinancialConditionTable>

          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => append(createCreditOperation())}
            disabled={disabled}
            sx={documentGenerationFormStyles.addButton}
          >
            Додати операцію
          </Button>
          <CreditOperationsTotals totals={totals} />
        </>
      )}
    </Box>
  );
};
